import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import { describeOptions, describePort } from '@app/common/util/uart/format';
import { UartService } from '@app/service/uart.service';
import {
  BAUD_RATE_PRESETS,
  UART_BUFFER_SIZE_MAX,
  UART_BUFFER_SIZE_MIN,
  UART_DATA_BITS_OPTIONS,
  UART_FLOW_CONTROL_OPTIONS,
  UART_PARITY_OPTIONS,
  UART_STOP_BITS_OPTIONS,
  UartOptions,
} from '@app/typedef/define/uart/UartOptions';

/**
 * 端口浮层里的一行。
 *
 * 端口名在 `computed` 里一次拼好，**模板里不再调函数** —— 模板里的函数调用
 * 每次变更检测都会重跑，而且语言切换时不会重算（`translate.instant` 不是响应式的）。
 */
interface UartPortItem {
  port: SerialPort;
  /** `0x1A86:0x7523 · 沁恒 WCH`；端口没有 USB 标识（板载 UART）时是「未知设备」。 */
  label: string;
  lastUsed: boolean;
  connected: boolean;
}

/**
 * 应用表头上的串口工具条（挂在 `main.component.html` 的 `nz-header` 里）。
 *
 * 它顶替的是原来那一整页「串口配置」：端口列表连同「忘记 / 选择串口 / 刷新」收进端口浮层，
 * 六项参数收进参数浮层。做这个合并的原因是**串口调试的真实节奏是「连上 → 发一包 → 看回什么
 * → 改个波特率 → 再看一包」**，而原来配置与调试分处两页，每跳一次，
 * 调试页那 2000 条记录连同定时发送的开关键就全丢了（日志是页面状态）。
 *
 * **放在布局层而不是某一页里**：串口连接是全局状态（`UartService` 是单例，跨路由不丢），
 * 工具条却只挂在调试页的话，切到 Modbus 点表就看不见连接状态了。它原来在调试页的
 * `nz-page-header` 里，那一页的抬头被去掉之后搬到了这里。
 *
 * **零 `@Input()`、零 `@Output()`**：它直接注入 `UartService`，要的状态全在服务里。
 * 没有输入输出，正说明它跟宿主页面之间没有耦合。
 */
@Component({
  selector: 'main-uart-bar',
  templateUrl: './uart-bar.component.html',
  styleUrl: './uart-bar.component.less',
  imports: [
    FormsModule,
    TranslatePipe,
    NzButtonModule,
    NzFormModule,
    NzIconModule,
    NzInputNumberModule,
    NzPopoverModule,
    NzSelectModule,
    NzTagModule,
    NzTooltipModule,
  ],
})
export class UartBarComponent {
  protected readonly uart = inject(UartService);
  private readonly translate = inject(TranslateService);

  protected readonly baudRatePresets = BAUD_RATE_PRESETS;
  protected readonly dataBitsOptions = UART_DATA_BITS_OPTIONS;
  protected readonly stopBitsOptions = UART_STOP_BITS_OPTIONS;
  protected readonly parityOptions = UART_PARITY_OPTIONS;
  protected readonly flowControlOptions = UART_FLOW_CONTROL_OPTIONS;
  protected readonly bufferMin = UART_BUFFER_SIZE_MIN;
  protected readonly bufferMax = UART_BUFFER_SIZE_MAX;

  /**
   * 语言切换信号。**只为了被下面的 `computed` 读到**，好让 `translate.instant`
   * 拼出来的文案在切语言时重算 —— 它本身不是响应式的，不给它一个依赖就永远是旧语言。
   */
  private readonly langChange = toSignal(this.translate.onLangChange);

  protected readonly options = this.uart.options;
  /** 当前参数摘要，`115200 8-N-1`。 */
  protected readonly summary = computed(() => describeOptions(this.options()));
  /** 是否在用「自定义波特率」而不是预设档位。初值看存下来的值在不在预设里。 */
  protected readonly customBaud = signal(!BAUD_RATE_PRESETS.includes(this.uart.options().baudRate));

  /** 环境不可用（浏览器没有 Web Serial，或页面不在安全上下文）。后面每一个操作都要看它。 */
  protected readonly unsupported = computed(() => this.uart.status() === 'unsupported');

  /*
   * 两个浮层的开合。
   *
   * `paramOpen` 只跟着 `(nzPopoverVisibleChange)` 走、不绑回 `[nzPopoverVisible]`：
   * 只是用来 `aria-expanded`，没有「程序控制开合」的需求。
   *
   * `portOpen` 是**受控绑定**（模板里 `[nzPopoverVisible]="portOpen()"`）：因为「点连接要自动收起
   * 浮层」，必须能程序化把 `portOpen` 置 false 让 ng-zorro 关闭浮层 —— 这正是 ng-zorro `popover-control`
   * 官方示例的写法（`trigger=click` + 受控 `visible`），不会跟其内部状态打架。
   */
  protected readonly portOpen = signal(false);
  protected readonly paramOpen = signal(false);

  protected readonly statusText = computed(() => {
    this.langChange();
    switch (this.uart.status()) {
      case 'unsupported':
        return this.translate.instant('不可用');
      case 'connecting':
        return this.translate.instant('连接中');
      case 'connected':
        return this.translate.instant('已连接');
      default:
        return this.translate.instant('未连接');
    }
  });

  protected readonly statusColor = computed<'success' | 'processing' | 'error' | 'default'>(() => {
    switch (this.uart.status()) {
      case 'connected':
        return 'success';
      case 'connecting':
        return 'processing';
      case 'unsupported':
        return 'error';
      default:
        return 'default';
    }
  });

  /** 已连接端口的显示名，未连接为 null。 */
  protected readonly activePort = computed(() => {
    const info = this.uart.portInfo();
    return info === null ? null : describePort(info);
  });

  /**
   * 端口按钮上的字：**连着哪个口就报哪个口的名字**，没连才是「选择串口」
   * （未连接时也得有字，否则那颗按钮是空的）。
   */
  protected readonly portLabel = computed(() => {
    this.langChange();
    if (!this.uart.connected()) {
      return this.translate.instant('选择串口');
    }
    // 连着的口也可能报不出名字（没有 USB VID/PID 的口，比如虚拟串口）：与端口列表里
    // 那一行用同一个兜底，而不是退回「选择串口」—— 连着呢，那颗按钮不能这么说。
    return this.translate.instant('串口：{{name}}', {
      name: this.activePort() ?? this.translate.instant('未知设备'),
    });
  });

  protected readonly portItems = computed<UartPortItem[]>(() => {
    this.langChange();
    return this.uart.grantedPorts().map((port) => ({
      port,
      label: describePort(port.getInfo()) ?? this.translate.instant('未知设备'),
      lastUsed: this.uart.isLastUsed(port),
      connected: this.uart.isConnected(port),
    }));
  });

  constructor() {
    /*
     * 进来先列一遍已授权的端口。**不会弹窗**，也不会自动连接 —— 见 `UartService` 的类注释。
     *
     * 这一句原来是配置页的构造函数在做的，而配置页已经被删掉了。剩下的调用点只有
     * `navigator.serial` 的 connect/disconnect 事件、`requestPort()` 与 `forget()` ——
     * 少了这一句，用户第一次点开端口浮层会看到「还没有已授权的串口」，哪怕明明授权过。
     */
    void this.uart.refreshGrantedPorts();
  }

  // ------------------------------------------------------------------ 端口

  /** 端口浮层打开时重刷一遍，保证每次看到的都是最新的（设备可能在页面开着的时候被插拔过）。 */
  protected onPortOpenChange(visible: boolean): void {
    this.portOpen.set(visible);
    if (visible) void this.uart.refreshGrantedPorts();
  }

  protected async choosePort(): Promise<void> {
    await this.uart.requestPort();
  }

  protected async refresh(): Promise<void> {
    await this.uart.refreshGrantedPorts();
  }

  /**
   * 点某一行的「连接」。
   *
   * 连接中的那个端口改过参数时，这里走 `reconnect()` 而不是 `connect()` —— 参数变了
   * 要重新 `open()` 才生效，而 `connect()` 对已打开的端口会先 `disconnect()` 再连，
   * 效果一样但会多绕一次；`reconnect()` 表达的就是「按新参数重开」这个意图。
   */
  protected async connect(port: SerialPort): Promise<void> {
    // 点「连接」即收起端口浮层 —— 连接是终点动作，关了就表明当前选中哪个口已明确。
    this.portOpen.set(false);
    if (this.uart.isConnected(port) && this.uart.needsReconnect()) {
      await this.uart.reconnect();
      return;
    }
    await this.uart.connect(port);
  }

  protected async disconnect(): Promise<void> {
    await this.uart.disconnect();
  }

  protected async reconnect(): Promise<void> {
    await this.uart.reconnect();
  }

  protected async forget(port: SerialPort): Promise<void> {
    await this.uart.forget(port);
  }

  // ------------------------------------------------------------------ 参数

  protected patch(patch: Partial<UartOptions>): void {
    this.uart.setOptions(patch);
  }

  protected resetOptions(): void {
    this.uart.resetOptions();
    this.customBaud.set(false);
  }

  /** `nz-input-number` 清空时给 null，落回默认波特率 —— 规范要求 `baudRate` 必填且为正数。 */
  protected setBaudRate(value: number | null): void {
    this.uart.setOptions({ baudRate: value ?? new UartOptions().baudRate });
  }

  /** 从「自定义」切回预设档位：当前值本来就不在预设里，只能回到默认那一档。 */
  protected usePresetBaud(): void {
    this.customBaud.set(false);
    this.uart.setOptions({ baudRate: new UartOptions().baudRate });
  }

  protected setBufferSize(value: number | null): void {
    const fallback = new UartOptions().bufferSize;
    this.uart.setOptions({ bufferSize: value ?? fallback });
  }
}
